import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

// Pop-up de l'accueil : pause hivernale 2026-2027. Affiché jusqu'à la réouverture du 18 avril 2027,
// puis il disparaît de lui-même. Une fois fermé, il ne revient pas pendant la visite (sessionStorage).
export const REOUVERTURE = Date.parse("2027-04-18T00:00:00+02:00");
const DISMISSAL_KEY = "syam_pause_hivernale_2026_dismissed";
const VILLA_IMAGE = "/media/visiter-video-first-frame-1280_a1dca58d.webp";

function isOpeningPopupActive() {
  return Date.now() < REOUVERTURE;
}

const POPUP_CSS = `
  @keyframes syam-opening-overlay-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes syam-opening-card-in {
    from { opacity: 0; transform: translateY(20px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .syam-opening-overlay {
    animation: syam-opening-overlay-in 220ms cubic-bezier(0.23, 1, 0.32, 1) both;
  }
  .syam-opening-card {
    animation: syam-opening-card-in 300ms cubic-bezier(0.23, 1, 0.32, 1) both;
  }
  .syam-opening-card:focus { outline: none; }
  .syam-opening-cta:hover { background: #9d7c43 !important; }
  .syam-opening-close:hover { background: rgba(14,12,10,0.62) !important; }
  @media (max-width: 640px) {
    .syam-opening-card { grid-template-columns: 1fr !important; max-height: calc(100vh - 2rem); overflow-y: auto; }
    .syam-opening-image { min-height: 205px; }
    .syam-opening-content { padding: 1.75rem 1.4rem 1.5rem !important; }
  }
  @media (prefers-reduced-motion: reduce) {
    .syam-opening-overlay, .syam-opening-card { animation: none !important; }
  }
`;

export function OpeningSeasonPopup() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpeningPopupActive() || sessionStorage.getItem(DISMISSAL_KEY) === "1") return;

    const timer = window.setTimeout(() => setVisible(true), 350);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) return;
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      sessionStorage.setItem(DISMISSAL_KEY, "1");
      setVisible(false);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible]);

  const close = () => {
    sessionStorage.setItem(DISMISSAL_KEY, "1");
    setVisible(false);
  };

  if (!visible || !isOpeningPopupActive()) return null;

  return (
    <>
      <style>{POPUP_CSS}</style>
      <div
        className="syam-opening-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="opening-popup-title"
        aria-describedby="opening-popup-details"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10020,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          background: "rgba(14, 12, 10, 0.7)",
          backdropFilter: "blur(4px)",
        }}
      >
        <button
          aria-label={t("opening_popup_close")}
          onClick={close}
          style={{ position: "absolute", inset: 0, border: 0, background: "transparent", cursor: "default" }}
        />
        <section
          className="syam-opening-card"
          tabIndex={-1}
          style={{
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateColumns: "minmax(250px, 1.05fr) minmax(280px, 0.95fr)",
            width: "min(760px, 100%)",
            background: "#f7f3ec",
            boxShadow: "0 28px 80px rgba(0,0,0,0.46)",
            overflow: "hidden",
          }}
        >
          <div className="syam-opening-image" style={{ position: "relative", minHeight: "360px", overflow: "hidden" }}>
            <img
              src={VILLA_IMAGE}
              alt={t("opening_popup_image_alt")}
              width={1280}
              height={720}
              fetchPriority="high"
              decoding="async"
              style={{ width: "100%", height: "100%", position: "absolute", inset: 0, objectFit: "cover", objectPosition: "60% center" }}
            />
          </div>

          <div className="syam-opening-content" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", padding: "2.7rem 2.6rem" }}>
            <button
              ref={closeButtonRef}
              className="syam-opening-close"
              onClick={close}
              aria-label={t("opening_popup_close")}
              style={{
                position: "absolute", top: "0.8rem", right: "0.8rem",
                width: 34, height: 34, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.48)",
                background: "rgba(14,12,10,0.42)", color: "#fff", cursor: "pointer",
                display: "grid", placeItems: "center", transition: "background 160ms ease-out",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2 2l10 10M12 2 2 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </button>

            <p style={{ margin: "0 0 0.55rem", fontFamily: "'Poppins', sans-serif", fontSize: "0.66rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#9d7c43" }}>
              {t("opening_popup_eyebrow")}
            </p>
            <h2 id="opening-popup-title" style={{ margin: "0 0 0.85rem", color: "#17120d", fontFamily: "'DM Serif Display', serif", fontSize: "clamp(1.9rem, 3vw, 2.15rem)", fontWeight: 400, lineHeight: 1.02 }}>
              {t("winter_popup_title")}
            </h2>
            <div id="opening-popup-details" style={{ color: "#5e554c", fontFamily: "'Poppins', sans-serif", fontSize: "0.91rem", lineHeight: 1.7 }}>
              <p style={{ margin: "0 0 0.7rem" }}>{t("winter_popup_text_1")}</p>
              <p style={{ margin: "0 0 0.7rem", color: "#17120d", fontWeight: 600 }}>{t("winter_popup_text_2")}</p>
              <p style={{ margin: "0 0 1.6rem" }}>{t("winter_popup_text_3")}</p>
            </div>
            <button
              className="syam-opening-cta"
              type="button"
              onClick={close}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", border: 0, cursor: "pointer",
                minHeight: 45, padding: "0.72rem 1.15rem", background: "#b8975a", color: "#fff",
                textDecoration: "none", fontFamily: "'Poppins', sans-serif", fontSize: "0.7rem",
                fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", transition: "background 160ms ease-out",
              }}
            >
              {t("winter_popup_cta")}
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
